import React from 'react';
import { IDocumentSavedSearch } from '@kurtosys/types/api/document/IDocumentSavedSearch.js';
import { action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import IconInput from '../IconInput/IconInput.js';
import Paging from '../Paging/Paging.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import { TranslationContext } from '../Translate/Translate.js';

import { ISavedSearchListConfiguration, ISavedSearchListProps, ISavedSearchListState } from './models/index.js';
import Delete from './styledComponents/Delete.js';
import NotificationOff from './styledComponents/NotificationOff.js';
import NotificationOn from './styledComponents/NotificationOn.js';
import Row from './styledComponents/Row.js';
import SavedSearchName from './styledComponents/SavedSearchName.js';
import Wrapper from './styledComponents/Wrapper.js';
import { SAVED_SEARCH_LIST_CATEGORY_KEY, SAVED_SEARCH_LIST_COMPONENT_KEY } from './SavedSearchList.meta.js';

export class SavedSearchList extends ComponentBase<
	ISavedSearchListProps,
	ISavedSearchListState,
	ISavedSearchListConfiguration
> {
	categoryKey = SAVED_SEARCH_LIST_CATEGORY_KEY;
	componentKey = SAVED_SEARCH_LIST_COMPONENT_KEY;

	constructor(props: ISavedSearchListProps) {
		super(props);

		makeObservable(this, {
			handleDelete: action,
			handleClick: action,
			handleToggleFrequency: action,
		});
	}

	handleDelete(savedSearch: IDocumentSavedSearch) {
		const { onDelete } = this.props;
		if (onDelete) {
			onDelete(savedSearch);
		}
	}

	handleClick(savedSearch: IDocumentSavedSearch) {
		const { onClick } = this.props;
		if (onClick) {
			onClick(savedSearch);
		}
	}

	handleToggleFrequency(savedSearch: IDocumentSavedSearch) {
		const { onToggleFrequency } = this.props;
		if (onToggleFrequency) {
			let { notificationFrequency } = savedSearch;
			switch (notificationFrequency) {
				case 'off':
					notificationFrequency = 'daily';
					break;
				case 'daily':
					notificationFrequency = 'weekly';
					break;
				case 'weekly':
					notificationFrequency = 'off';
					break;
			}
			onToggleFrequency(savedSearch, notificationFrequency);
		}
	}

	renderSavedSearch(savedSearch: IDocumentSavedSearch, translationContext: ITranslationContextValue) {
		const { notificationFrequency, savedSearchName, savedSearchId } = savedSearch;
		const NotificationPill = notificationFrequency === 'off' ? NotificationOff : NotificationOn;
		const asset =
			notificationFrequency === 'off'
				? 'base.savedSearchList.notificationOff'
				: 'base.savedSearchList.notificationOn';
		return (
			<Row key={savedSearchId}>
				<SavedSearchName onClick={() => this.handleClick(savedSearch)}>{savedSearchName}</SavedSearchName>
				<NotificationPill
					value={this.translateValue(translationContext, notificationFrequency)}
					icon={{ asset }}
					onClick={() => this.handleToggleFrequency(savedSearch)}
				/>
				<Delete asset={'base.savedSearchList.delete'} onClick={() => this.handleDelete(savedSearch)} />
			</Row>
		);
	}

	translateValue(translationContext: ITranslationContextValue, value?: string) {
		return value ? translationContext.translate(value) : value;
	}

	render() {
		const { className, paging, search, savedSearches = [] } = this.props;
		return (
			<Wrapper className={className}>
				<TranslationContext.Consumer>
					{(translationContext: ITranslationContextValue) => {
						if (search) {
							search.placeholder = this.translateValue(translationContext, search.placeholder);
						}
						return (
							<React.Fragment>
								{search && <IconInput {...search} />}
								{savedSearches.map((savedSearch) =>
									this.renderSavedSearch(savedSearch, translationContext),
								)}
								{paging && <Paging {...paging} />}
							</React.Fragment>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(SavedSearchList),
	SAVED_SEARCH_LIST_CATEGORY_KEY,
	SAVED_SEARCH_LIST_COMPONENT_KEY,
);
